import React, { useState, useEffect, useRef } from "react";
import { QrReader } from "react-qr-reader";
import { Link } from "react-router-dom";
import { isSmartCard } from '../smart/model';
import {
  Typography,
  Box,
  Paper,
  CssBaseline,
  Stack,
  Alert,
  Container,
  Fade,
  Button,
  Card
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import ColorModeSelect from './shared-theme/ColorModeSelect';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AddQrContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'fixed',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  minHeight: '100dvh',
  width: '100%',
  position: 'relative',
  padding: 0,
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
  [theme.breakpoints.up('sm')]: {
    padding: 0,
  },
}));

const VideoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  background: theme.palette.background.paper,
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  padding: theme.spacing(0),
  marginTop: theme.spacing(0),
  aspectRatio: '1 / 1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  '& section': {
    width: '100%',
    height: '100%',
  },
  '& div': {
    width: '100%',
    height: '100%',
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 1),
  marginBottom: theme.spacing(3),
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

function AddQrCodePage() {
  const [qrCodeId, setQrCodeId] = useState("");
  const [qrType, setQrType] = useState("");
  const [error, setError] = useState("");
  const mediaStreamRef = useRef(null);
  const intervalRef = useRef(null);

  const cleanupAndNavigate = (targetPath) => {
    // Store the target path in localStorage
    localStorage.setItem('qr_redirect_path', targetPath);
    
    try {
      // Stop all tracks from the media stream
      if (mediaStreamRef.current) {
        const tracks = mediaStreamRef.current.getTracks();
        tracks.forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
        mediaStreamRef.current = null;
      }

      // Additional cleanup for any remaining video elements
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(video => {
        if (video.srcObject) {
          const tracks = video.srcObject.getTracks();
          tracks.forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
            }
          });
          video.srcObject = null;
        }
      });

      // Clear the style application interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (err) {
      console.error('Error during camera cleanup:', err);
    }

    // Force reload which will trigger the navigation effect
    window.location.reload();
  };

  // Handle navigation after reload
  useEffect(() => {
    const redirectPath = localStorage.getItem('qr_redirect_path');
    if (redirectPath) {
      localStorage.removeItem('qr_redirect_path');
      window.location.href = redirectPath;
    }
  }, []);

  const handleDecode = (result) => {
    if (result) {
      try {
        const urlObj = new URL(result.text);
        const qrCodeId = urlObj.searchParams.get('id');
        const qrType = urlObj.searchParams.get("t");
        setQrCodeId(qrCodeId);
        setQrType(qrType);
      } catch (err) {
        setError("Invalid QR code format. Please try again.");
      }
    }
  };

  // Style application effect
  useEffect(() => {
    const applyVideoStyles = () => {
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(video => {
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.display = 'block';
      });
      
      const scannerSections = document.querySelectorAll('.react-qr-reader section');
      scannerSections.forEach(section => {
        section.style.width = '100%';
        section.style.height = '100%';
      });
    };

    // Initial style application
    setTimeout(applyVideoStyles, 500);
    
    // Set up interval for style application
    intervalRef.current = setInterval(applyVideoStyles, 1000);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Navigation and cleanup effect
  useEffect(() => {
    if (qrCodeId) {
      if (isSmartCard(qrCodeId, qrType)) {
        cleanupAndNavigate(`/studio/activate?id=${encodeURIComponent(qrCodeId)}`);
      } else if(qrType === "gr") {
        cleanupAndNavigate(`/google-review-config?id=${qrCodeId}&t=${qrType}`);
      } else {
        setError("Unsupported QR code type. Please try a different QR code.");
      }
    }
  }, [qrCodeId, qrType]);

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <AddQrContainer>
        <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1 }} />
        <StyledPaper>
          <Container maxWidth="xs" sx={{ py: 4 }}>
            <Fade in timeout={800}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <BackButton
                  component={Link}
                  to="/profile"
                  startIcon={<ArrowBackIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    cleanupAndNavigate('/profile');
                  }}
                >
                  Back to Stores
                </BackButton>

                <Typography
                  variant="h5"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  <QrCodeScannerIcon color="primary" sx={{ fontSize: 28 }} />
                  Add Store QR Code
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Please use your device's camera to scan the QR code on the stand.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ width: '100%', position: 'relative' }}>
                  <VideoCard elevation={0}>
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                      <QrReader
                        onResult={handleDecode}
                        constraints={{
                          facingMode: "environment",
                          width: { min: 640, ideal: 1280, max: 1920 },
                          height: { min: 480, ideal: 720, max: 1080 },
                          aspectRatio: { ideal: 1.7777777778 },
                          focusMode: 'continuous',
                          exposureMode: 'continuous',
                          whiteBalanceMode: 'continuous',
                          frameRate: { ideal: 30 }
                        }}
                        scanDelay={500}
                        videoContainerStyle={{
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        videoStyle={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onInit={(error, result) => {
                          if (result) {
                            mediaStreamRef.current = result?.video?.srcObject;
                            // Apply additional video track settings
                            const videoTrack = result.video.srcObject.getVideoTracks()[0];
                            if (videoTrack) {
                              try {
                                const capabilities = videoTrack.getCapabilities();
                                const settings = {};
                                
                                // Set focus mode if available
                                if (capabilities.focusMode && capabilities.focusMode.includes('continuous')) {
                                  settings.focusMode = 'continuous';
                                }
                                
                                // Set brightness if available
                                if (capabilities.brightness) {
                                  settings.brightness = capabilities.brightness.max * 0.75;
                                }
                                
                                // Set contrast if available
                                if (capabilities.contrast) {
                                  settings.contrast = capabilities.contrast.max * 0.6;
                                }

                                // Apply the settings
                                if (Object.keys(settings).length > 0) {
                                  videoTrack.applyConstraints({ advanced: [settings] });
                                }
                              } catch (err) {
                                console.error('Error applying video track settings:', err);
                              }
                            }
                          }
                        }}
                        ViewFinder={() => null}
                      />
                    </div>
                  </VideoCard>
                </Box>
              </Box>
            </Fade>
          </Container>
        </StyledPaper>
      </AddQrContainer>
    </AppTheme>
  );
}

export default AddQrCodePage;
